<template>
  <Panel size="small">
    <template #header>
      <span data-cy="heading">Budget</span>
    </template>

    <template #content>
      <!-- CPA Info Banner - shown when category is CPA-only -->
      <b-alert
        v-if="isCpaOnlyCategory"
        show
        variant="info"
        class="mb-3"
      >
        <font-awesome-icon
          :icon="['fas', 'info-circle']"
          class="mr-2"
        />
        <strong>CPA Billing:</strong> This category uses Cost Per Acquisition billing. You will only be charged when a conversion event occurs, not based on call duration.
      </b-alert>

      <!-- Minimum Duration - hidden for CPA categories since billing is conversion-based -->
      <BaseFormToggle
        v-if="!isRTBSelected && !isCpaOnlyCategory"
        id="buyer-create-campaign__audience__minimum-duration"
        v-model="minimumDurationSelected"
        label="Minimum Duration"
        data-cy="toggle-minimum-duration"
        internal-platform-input
        :checked-value="true"
        :unchecked-value="false"
        has-tooltip
      >
        <template 
          v-if="minimumDurationSelected"
          #extra
        >          
          <BaseFormInput
            v-model="minimumDurationInSeconds"              
            form-group-class="mb-0"
            type="number"
            max="9999"
            required
            internal-platform-input
            small
            horizontal
            width="7"
            width-lg="5"
            label-class="d-none"
            name="Minimum Duration"
            rules="min_value:0|max_value:9999"
          >
            <template #helpText>
              <b-form-text class="col-1">
                <div>
                  seconds
                </div>
              </b-form-text>
            </template>
          </BaseFormInput>                    
        </template>
        <template
          v-if="showMinimumDurationWarning"
          #helpText
        >
          <b-badge
            variant="warning"
            tag="div"
            class="mt-3"
            data-cy="badge-minimum-duration-warning"
          >
            <font-awesome-icon
              :icon="['fas', 'exclamation-triangle']"
              :style="{ cursor: 'pointer' }"
            />
            <template v-if="minimumDurationSelected">
              We recommend minimum duration of at least {{ minimumDurationWarningThresholdSeconds }} seconds
            </template>
            <template v-else>
              Without minimum call duration limit you pay for every call that connects
            </template>
          </b-badge>
        </template>
      </BaseFormToggle>

      <!-- A Programmatic campaign renders no Bid Amount input, so its market card takes a row of its
           own here, above Daily Budget (§3.6) -->
      <b-row v-if="showsMarketIntelligenceCardAboveDailyBudget">
        <b-col md="3" />
        <b-col
          md="9"
          lg="7"
          class="mb-3"
        >
          <MarketIntelligenceCard
            v-bind="marketIntelligenceCardProps"
            v-on="marketIntelligenceCardListeners"
          />
        </b-col>
      </b-row>

      <BaseCurrencyInput
        id="buyer-create-campaign__audience__daily-budget"
        v-model="dailyBudgetInUSD"
        internal-platform-input
        horizontal
        data-cy="input-daily-budget"
        small
        name="Daily Budget"
        placeholder="ex. $1000.00"
        rules="amount_range:10,99999.99"
        label="Daily Budget"
        required
        width="2"
        width-lg="2"
        width-xs="4"
        input-class="fixed-width-130"
        has-tooltip
      />

      <BaseFormToggle        
        id="buyer-create-campaign__audience__hourly-budget-selected"
        v-model="hourlyBudgetSelected"
        label="Hourly Budget"
        data-cy="toggle-hourly-budget"
        internal-platform-input
        :checked-value="true"
        :unchecked-value="false"
        has-tooltip
      >
        <template 
          v-if="hourlyBudgetSelected"
          #extra
        >          
          <BaseCurrencyInput
            id="buyer-create-campaign__audience__hourly-budget"
            v-model="hourlyBudgetInUSD"
            internal-platform-input
            horizontal
            label-class="d-none"
            data-cy="input-hourly-budget"
            small
            name="Hourly Budget"   
            placeholder="ex. $100.00"
            rules="amount_range:10,99999.99"         
            required
            width="2"
            width-lg="2"
            width-xs="4"
            input-class="fixed-width-130"            
          />
        </template>
      </BaseFormToggle>            

      <BaseCurrencyInput
        v-if="!isCpaRTBCampaign && !isProgrammaticSelected"
        id="buyer-create-campaign__audience__bid-per-call"
        v-model="maxBidPerCallInUSD"
        :horizontal="true"
        internal-platform-input
        small
        data-cy="input-bid-per-call"
        name="Bid Per Call"
        label="Bid Amount"
        placeholder="ex. $100.00"
        min="0"
        :rules="bidPerCallRules"
        :mode="validateBidOnSave"
        :max-value="9999.99"
        v-bind="maxBidAttributes"
        width="6"
        width-lg="6"
        width-xs="12"
        input-class="fixed-width-130"
        has-tooltip
        :disabled="isRTBSelected"
      >
        <template #helpText>
          <!-- The market card's guidance takes its place where the card suggests a setup (§3.6) -->
          <b-form-text v-if="isRTBSelected && !showsMarketIntelligenceGuidance">
            <font-awesome-icon
              :icon="['fas', 'exclamation-triangle']"
              class="mr-1"
            />
            <i>Bid amount and duration will come from the bids in Ringba RTB.</i>
          </b-form-text>
        </template>
      </BaseCurrencyInput>

      <!-- The market card, behind rx-market-intelligence, under the Bid Amount input for Static and
           RingbaRTB (disabled there); Programmatic's is above Daily Budget -->
      <b-row v-if="showsMarketIntelligenceCardUnderBid">
        <b-col md="3" />
        <b-col
          md="9"
          lg="7"
          class="mb-3"
        >
          <MarketIntelligenceCard
            v-bind="marketIntelligenceCardProps"
            v-on="marketIntelligenceCardListeners"
          />
        </b-col>
      </b-row>

      <!-- Live market context - only where the buyer actually types a static bid, never for a
           softphone agent (see marketContext/canViewMarketContext). The fallback for the card
           above while rx-market-intelligence is off, or while the backend is older than the card -->
      <b-row v-if="showBidMarketContext">
        <b-col md="3" />
        <b-col
          md="9"
          lg="7"
          class="mb-3"
        >
          <BidMarketContextPanel
            :category="selectedCategory"
            :country-id="buyerCampaign.countryId"
            :language-id="buyerCampaign.languageId"
            :current-bid="maxBidPerCallInUSD"
            @select-bid="onSelectBid"
          />
        </b-col>
      </b-row>

      <!-- CPA Conversion Toggle - only shown for CPA-supporting categories -->
      <BaseFormToggle
        v-if="isCpaOnlyCategory"
        id="buyer-create-campaign__budget__cpa-conversion"
        v-model="isPostbackConversion"
        label="CPA Conversion"
        data-cy="toggle-cpa-conversion"
        internal-platform-input
        :checked-value="true"
        :unchecked-value="false"
        disabled
        has-tooltip
      >
        <template #helpText>
          <b-form-text class="mt-3">
            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              class="mr-1"
            />
            <i>Required for this category - billing is based on CPA conversions only.</i>
          </b-form-text>
        </template>
      </BaseFormToggle>

      <!-- Default Postback Bid Amount - only shown for CPA categories with RingbaRTB routing -->
      <BaseCurrencyInput
        v-if="isCpaRTBCampaign"
        id="buyer-create-campaign__budget__default-postback-bid-amount"
        v-model="defaultPostbackBidAmount"
        :horizontal="true"
        internal-platform-input
        small
        data-cy="input-default-postback-bid-amount"
        name="Bid Amount"
        label="Bid Amount"
        placeholder="ex. $100.00"
        rules="amount_range:0.01,9999.99"
        :max-value="9999.99"
        required
        width="6"
        width-lg="6"
        width-xs="12"
        input-class="fixed-width-130"
        has-tooltip
      >
        <template #helpText>
          <b-form-text>
            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              class="mr-1"
            />
            <i>The static bid amount for postback conversions. Currently only static bids are supported for CPA conversions.</i>
          </b-form-text>
        </template>
      </BaseCurrencyInput>

      <b-row>
        <b-col md="3" />
        <b-col
          v-if="submitted && invalid"
          class="text-danger"
        >
          Please fix the errors in your form
        </b-col>
      </b-row>

      <BaseButtonLoader
        :horizontal="true"
        data-cy="button-create-campaign"
        internal-platform-input
        variant="primary"
        :loading="loading"
        :disabled="!minimumInitialDepositSatisfied"
        type="submit"
      >
        {{ edit ? 'Save' : 'Create' }} Campaign
      </BaseButtonLoader>

      <b-row v-if="!edit">
        <b-col md="3" />
        <b-col>
          <b-alert
            show
            variant="info"
            class="mb-0 py-2"
            style="margin-left: -5px;"
            data-cy="alert-campaign-paused"
          >
            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              class="mr-2"
            />
            <template v-if="softphone">
              Note that the campaign you created will remain paused until Ringba X reviews it. After this
              campaign is approved, click Go Live on the campaigns list when you are ready to take
              calls.
            </template>
            <template v-else>
              Note that approved campaign will be paused. You can resume it when ready to receive calls.
            </template>
          </b-alert>
        </b-col>
      </b-row>
    </template>
  </Panel>
</template>

<script>

import { showErrorMessage } from '@/notification-utils'
import { mapFeatureFlags } from '@/plugins/featureFlags'
import { getOfferedSuggestion } from '@/store/modules/market-context.store'
import { FEATURE_FLAG_KEYS } from '@/utils/featureFlagKeys'
import { currencyFormatter } from '@/utils/formatCurrency'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'

import { MINIMUM_DURATION_WARNING_THRESHOLD_SECONDS, isMinimumDurationTooShort } from '@/utils/minimumDuration'

import BidMarketContextPanel from './BidMarketContextPanel.vue'
import MarketIntelligenceCard from '@/components/buyer/market/MarketIntelligenceCard.vue'

export default {
  name: 'Budget',
  components: {
    BidMarketContextPanel,
    MarketIntelligenceCard
  },
  props: {
    invalid: {
      type: Boolean,
      default: true
    },

    edit: {
      type: Boolean,
      default: false
    },

    loading: {
      type: Boolean,
      default: false
    },

    minimumInitialDepositSatisfied: {
      type: Boolean,
      default: false
    },

    selectedCategory: {
      type: Object,
      default: null
    },

    // A softphone campaign is never paused or resumed by hand - the agent goes live and offline
    // instead, and the list hides Pause/Resume for it - so the note about resuming would send
    // them looking for a button that is not rendered for their campaigns.
    softphone: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      submitted: false,
      minimumDurationWarningThresholdSeconds: MINIMUM_DURATION_WARNING_THRESHOLD_SECONDS,
    }
  },

  computed: {
    ...mapState('campaign', ['buyerCampaign', 'buyerCampaignMarketUsage']),

    ...mapState('common-entities', ['countries']),

    ...mapState('marketContext', ['categoryMarketContext', 'marketIntelligenceContext', 'marketIntelligenceUnavailable', 'campaignMarketPositions']),

    ...mapGetters('marketContext', ['canViewMarketContext']),

    ...mapFeatureFlags({
      marketIntelligenceEnabled: FEATURE_FLAG_KEYS.marketIntelligence.key
    }),

    isRTBSelected() {
      return this.buyerCampaign?.routing?.type === 'RingbaRTB' || this.buyerCampaign?.routing?.type === 'Programmatic'
    },

    isProgrammaticSelected() {
      return this.buyerCampaign?.routing?.type === 'Programmatic'
    },

    showMinimumDurationWarning() {
      return !this.minimumDurationSelected || isMinimumDurationTooShort(this.minimumDurationInSeconds)
    },

    // Known only for a saved campaign, whose category arrives with its bid preferences, and only
    // while the buyer keeps that category; the category list carries no minimum
    categoryMinBid() {
      const category = this.buyerCampaign?.category
      if (this.isRTBSelected || !category || category.id !== this.buyerCampaign.categoryId) return 0

      return Number(category.bidPreferences?.minBid) || 0
    },

    bidPerCallRules() {
      return this.categoryMinBid > 0
        ? `amount_range:0.01,9999.99|min_bid:${this.categoryMinBid}`
        : 'amount_range:0.01,9999.99'
    },

    // `isProgrammaticStaticBid`, which this used to read beside isRTBSelected, is defined nowhere
    // and was always undefined, so leaving it out changes nothing
    maxBidAttributes() {
      return !this.isRTBSelected ? { required: '' } : {}
    },

    minimumDurationSelected: {
      get() {
        return this.buyerCampaign.budget.minimumDurationSelected
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.minimumDurationSelected = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    minimumDurationInSeconds: {
      get() {
        return this.buyerCampaign.budget.minimumDurationInSeconds
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.minimumDurationInSeconds = Number(value)
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    dailyBudgetInUSD: {
      get() {
        return this.buyerCampaign.budget.dailyBudgetInUSD
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.dailyBudgetInUSD = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    maxBidPerCallInUSD: {
      get() {
        return this.buyerCampaign.budget.maxBidPerCallInUSD
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.maxBidPerCallInUSD = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    hourlyBudgetSelected: {
      get() {
        return this.buyerCampaign.budget.hourlyBudgetSelected
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.hourlyBudgetSelected = value
        budget.hourlyBudgetInUSD = currencyFormatter(budget.hourlyBudgetInUSD)
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    hourlyBudgetInUSD: {
      get() {
        return this.buyerCampaign.budget.hourlyBudgetInUSD
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.hourlyBudgetInUSD = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    isCpaOnlyCategory() {
      return this.selectedCategory?.isCpaConversionOnly === true
    },

    // Gated on the USER, not the `softphone` prop: the softphone edit page does not pass that prop.
    // The panel fetches from its own watcher, so with this false nothing is requested either, and a
    // late turn to true both mounts it and fetches. Last of all comes the market card, which takes
    // the panel's place inside the rx-market-intelligence rollout, so that flag, whose first read
    // logs a Datadog exposure, is read only where the panel would otherwise show.
    showBidMarketContext() {
      return this.canViewMarketContext
        && !this.isRTBSelected && !this.isCpaRTBCampaign && Boolean(this.selectedCategory)
        && !this.showMarketIntelligenceCard
    },

    // The market card (market intelligence, suggested bid iteration 1): the panel's gate with its
    // routing-type part lifted, so the card shows for every routing type but CPA-RTB, a postback
    // bid that keeps today's exclusion (§3.6). Not once the backend has answered without the card's
    // fields (marketIntelligenceUnavailable): the panel stays then, as today.
    // rx-market-intelligence is read last, where it decides what shows: card or panel for a
    // Static campaign, card or nothing for RingbaRTB and Programmatic.
    showMarketIntelligenceCard() {
      return this.canViewMarketContext
        && !this.isCpaRTBCampaign && Boolean(this.selectedCategory)
        && !this.marketIntelligenceUnavailable
        && this.marketIntelligenceEnabled
    },

    // The card renders once its response is in, as the panel does: nothing while it loads
    showsMarketIntelligenceCardContent() {
      return this.showMarketIntelligenceCard && Boolean(this.marketIntelligenceContext)
    },

    // Static and RingbaRTB: under the Bid Amount input (disabled for RingbaRTB)
    showsMarketIntelligenceCardUnderBid() {
      return this.showsMarketIntelligenceCardContent && !this.isProgrammaticSelected
    },

    // Programmatic renders no Bid Amount input: a row of its own above Daily Budget
    showsMarketIntelligenceCardAboveDailyBudget() {
      return this.showsMarketIntelligenceCardContent && this.isProgrammaticSelected
    },

    // A suggestion on screen, whose guidance replaces RingbaRTB's "bid amount and duration will come
    // from the bids" help text. Without one the card has no guidance, and the help text stays.
    showsMarketIntelligenceGuidance() {
      return this.showsMarketIntelligenceCardContent && Boolean(this.marketIntelligenceContext.suggestion)
    },

    // The card's market, or '' while the card is not shown. Its fetch keys on this, so it runs only
    // where the card shows: the panel fetches for itself, and only one of the two is ever on screen.
    marketIntelligenceRequestKey() {
      if (!this.showMarketIntelligenceCard) {
        return ''
      }

      return [this.selectedCategory.id, this.buyerCampaign.countryId, this.buyerCampaign.languageId]
        .map(part => part || '')
        .join(':')
    },

    countryName() {
      return (this.countries || []).find(country => country.id === this.buyerCampaign.countryId)?.name || ''
    },

    languageName() {
      return (this.selectedCategory?.languages || []).find(language => language.id === this.buyerCampaign.languageId)?.name || ''
    },

    // The campaign's own E1 row for the card (F5's follow-up; R59): the row the Campaigns list shows,
    // so the card and the list never disagree about a campaign. Null on create, which has no row, and
    // until the rows load
    campaignMarketPosition() {
      const campaignId = this.buyerCampaign?.id

      return campaignId ? (this.campaignMarketPositions[campaignId] || null) : null
    },

    marketIntelligenceCardProps() {
      return {
        context: this.marketIntelligenceContext,
        categoryName: this.selectedCategory?.name || '',
        categoryId: this.selectedCategory?.id || null,
        countryName: this.countryName,
        languageName: this.languageName,
        bid: this.maxBidPerCallInUSD,
        isCpaOnly: this.isCpaOnlyCategory,
        minimumDurationSeconds: this.minimumDurationSelected ? Number(this.minimumDurationInSeconds) : null,
        routingType: this.buyerCampaign?.routing?.type,
        programmaticBidSettings: this.buyerCampaign?.routing?.programmaticTarget?.bidSettings || null,
        marketPosition: this.campaignMarketPosition
      }
    },

    marketIntelligenceCardListeners() {
      return {
        'select-setup': this.onSelectSetup,
        'select-bid': this.onSelectBid
      }
    },

    // The card or the panel on screen with data, and what it offers, for the save's usage event
    // (docs/MARKET_CONTEXT_USAGE_ANALYTICS.md). Null while neither is
    marketUsageShown() {
      if (this.showsMarketIntelligenceCardContent) {
        const offered = getOfferedSuggestion({
          suggestion: this.marketIntelligenceContext.suggestion,
          marketPosition: this.campaignMarketPosition,
          isCpaOnly: this.isCpaOnlyCategory
        })

        return { surface: 'card', suggestedBidInUSD: offered?.bidInUSD ?? null, suggestedMinimumSeconds: offered?.minimumSeconds ?? null }
      }

      if (this.showBidMarketContext && this.categoryMarketContext) {
        const suggestedTier = (this.categoryMarketContext.tiers || []).find(tier => tier.isSuggested)

        return { surface: 'panel', suggestedBidInUSD: suggestedTier?.bidInUSD ?? null, suggestedMinimumSeconds: null }
      }

      return null
    },

    // Changes when what shows changes, and when loading the campaign drops the record: the edit
    // pages reload it after a save, with the card still on screen
    marketUsageRecordKey() {
      return this.marketUsageShown && JSON.stringify([this.marketUsageShown, this.buyerCampaignMarketUsage === null])
    },

    isCpaRTBCampaign() {
      return this.isCpaOnlyCategory && this.buyerCampaign?.routing?.type === 'RingbaRTB'
    },

    isPostbackConversion: {
      get() {
        return this.buyerCampaign.budget.isPostbackConversion
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.isPostbackConversion = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    defaultPostbackBidAmount: {
      get() {
        return this.buyerCampaign.routing.ringbaRTB?.defaultPostbackBidAmount
      },
      set(value) {
        const routing = {
          ...this.buyerCampaign.routing,
          ringbaRTB: {
            ...this.buyerCampaign.routing.ringbaRTB,
            defaultPostbackBidAmount: value
          }
        }
        this.UPDATE_BUYER_CAMPAIGN_ROUTING(routing)
      }
    },
  },

  watch: {
    // Kept while the step is left, so a save counts the card or panel the visit showed
    marketUsageRecordKey: {
      immediate: true,
      handler(key) {
        if (key) {
          this.SET_BUYER_CAMPAIGN_MARKET_SHOWN(this.marketUsageShown)
        }
      }
    },

    // The card's one fetch, the panel's watcher's counterpart: when the card comes on screen, which
    // is also when Datadog's answer turns rx-market-intelligence on late, and when its market
    // changes. Turning to '' needs nothing: the panel, if it takes the card's place, fetches itself.
    marketIntelligenceRequestKey: {
      immediate: true,
      handler(key) {
        if (key) {
          this.loadMarketIntelligence()
        }
      }
    },

    isCpaOnlyCategory: {
      immediate: true,
      handler(isCpaOnly) {
        if (isCpaOnly && !this.buyerCampaign.budget.isPostbackConversion) {
          const budget = { ...this.buyerCampaign.budget, isPostbackConversion: true }
          this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
        } else if (!isCpaOnly && this.buyerCampaign.budget.isPostbackConversion) {
          const budget = { ...this.buyerCampaign.budget, isPostbackConversion: false }
          this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
        }
      }
    }
  },

  methods: {
    ...mapMutations('campaign', [
      'UPDATE_BUYER_CAMPAIGN_BUDGET',
      'UPDATE_BUYER_CAMPAIGN_ROUTING',
      'SET_BUYER_CAMPAIGN_MARKET_SHOWN',
      'SET_BUYER_CAMPAIGN_MARKET_USE'
    ]),

    ...mapActions('marketContext', ['fetchCategoryMarketContext', 'clearCategoryMarketContext', 'fetchCampaignMarketPositions']),

    // `button` names the card's or the panel's button that set it (`setup`, `tile`, `panel`), kept
    // for the save's usage event
    onSelectBid(bidInUSD, button) {
      // BaseCurrencyInput only formats on user input events, so store the
      // app-standard currency string rather than the raw number. Suggestions carry
      // cents, and currencyFormatter keeps the decimals it is given, so fix them at
      // two: a $25.10 suggestion must not land in the input as "$25.1"
      this.maxBidPerCallInUSD = currencyFormatter(bidInUSD.toFixed(2))
      this.SET_BUYER_CAMPAIGN_MARKET_USE({ button: button || null, bidInUSD, minimumSeconds: null })
    },

    // The card's "Use these" (R10): the bid, stored as onSelectBid stores it, and Minimum Duration
    // turned on at the suggested minimum, in one budget update
    onSelectSetup({ bid, minimumSeconds }, button) {
      const budget = {
        ...this.buyerCampaign.budget,
        maxBidPerCallInUSD: currencyFormatter(bid.toFixed(2))
      }

      if (minimumSeconds != null) {
        budget.minimumDurationSelected = true
        budget.minimumDurationInSeconds = Number(minimumSeconds)
      }

      this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      this.SET_BUYER_CAMPAIGN_MARKET_USE({ button: button || null, bidInUSD: bid, minimumSeconds: minimumSeconds ?? null })
    },

    async loadMarketIntelligence() {
      const { countryId, languageId } = this.buyerCampaign

      // The market cell is all three of category, country and language, as for the panel: a
      // partial selection names no market, and the previous one's numbers must not stay on screen
      if (!this.selectedCategory?.id || !countryId || !languageId) {
        this.clearCategoryMarketContext()

        return
      }

      // The campaign's own row on the edit page (F5's follow-up; R59): E1 for the account, the
      // Campaigns list's call, read alongside the market. A helper like the market itself: a failed
      // read leaves the card without the campaign's terms and advice, as on create
      if (this.buyerCampaign?.id) {
        this.fetchCampaignMarketPositions().catch(() => null)
      }

      try {
        await this.fetchCategoryMarketContext({ categoryId: this.selectedCategory.id, countryId, languageId })
      } catch (error) {
        // Market context is a helper, never a blocker - the bid input works without it
        showErrorMessage(error.response?.data?.title || 'Failed to load market context')
      }
    },

    // vee-validate interaction mode for the bid: not checked while the buyer types, only when they
    // save; once Save has shown an error it re-checks on every keystroke, so a fixed bid clears it
    validateBidOnSave({ errors }) {
      return { on: errors.length ? ['input'] : [] }
    },

    submit() {
      this.submitted = true
      if (!this.invalid) {
        this.$emit('submit')
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
